/**
 * Optional live guest registry via Firebase (Firestore + Auth).
 *
 * Setup (once):
 * 1. Create a Firebase project → enable Firestore + Authentication (Google).
 * 2. Firestore rules: see `firestore.rules` in the repo (public guest-registry read; writes and
 *    config/registryAdmins read only for emails listed in allowedEmails).
 * 3. Authentication: add authorized domain (e.g. almostsilver.town). Sign in at /admin (see adminPath.js) with
 *    Google.
 * 4. Set env vars in CI / hosting (Vercel, Netlify, GitHub Actions secrets → build):
 *    REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_AUTH_DOMAIN, REACT_APP_FIREBASE_PROJECT_ID,
 *    REACT_APP_FIREBASE_APP_ID
 *    Optional: REACT_APP_FIREBASE_STORAGE_BUCKET (defaults to {PROJECT_ID}.firebasestorage.app; use …appspot.com for older buckets)
 *    Optional: REACT_APP_FIREBASE_GUEST_REGISTRY_PATH (default weddingGuestRegistry/canonical)
 *
 * First save from admin creates the document; all site visitors receive updates in real time.
 * Writes use a transaction: document existence plus `updatedAt` must match what this tab last received, so an
 * outdated or cached tab cannot overwrite a newer Firestore document (reload to sync, then save).
 */

import { initializeApp, getApps } from 'firebase/app';
import { getStorage } from 'firebase/storage';
import {
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  getDocFromServer,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ackRemoteRegistryDocUpdatedAt,
  applyRemoteGuestRegistryListenError,
  applyRemoteGuestRegistrySnapshot,
  coerceFirestoreGuestsToArray,
  getRemoteRegistryDocExistsForWriteGuard,
  getRemoteRegistryWriteBaseline,
  isRemoteGuestRegistryConfigured,
} from './guestAllowlist';

const PATH_DEFAULT = 'weddingGuestRegistry/canonical';

/** Compare Firestore Timestamp instances (handles cache/server snapshot shape drift). */
function firestoreTimestampsEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a.isEqual === 'function') {
    try {
      return a.isEqual(b);
    } catch {
      /* fall through */
    }
  }
  const as = a.seconds ?? a._seconds;
  const bs = b.seconds ?? b._seconds;
  const an = a.nanoseconds ?? a._nanoseconds ?? 0;
  const bn = b.nanoseconds ?? b._nanoseconds ?? 0;
  return as === bs && an === bn;
}

/**
 * Storage bucket id only (no gs://). Must match Project settings → Your apps → storageBucket.
 * New projects often use *.firebasestorage.app; older ones *.appspot.com.
 */
function firebaseStorageBucketId() {
  const explicit = (process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '').trim().replace(/^gs:\/\//, '');
  if (explicit) return explicit;
  const projectId = (process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim();
  if (!projectId) return '';
  return `${projectId}.firebasestorage.app`;
}

function firebaseConfig() {
  const projectId = (process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim();
  const storageBucket = firebaseStorageBucketId();
  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    ...(storageBucket ? { storageBucket } : {}),
  };
}

function parseRegistryPath() {
  const raw = (process.env.REACT_APP_FIREBASE_GUEST_REGISTRY_PATH || PATH_DEFAULT).trim();
  const parts = raw.split('/').filter(Boolean);
  if (parts.length >= 2) {
    return { collectionId: parts[0], docId: parts[1] };
  }
  return { collectionId: 'weddingGuestRegistry', docId: 'canonical' };
}

let appSingleton;
let dbSingleton;
let authSingleton;
let storageSingleton;
let unsubSnapshot = null;

function getFirebaseApp() {
  if (appSingleton) return appSingleton;
  const cfg = firebaseConfig();
  if (!cfg.apiKey || !cfg.projectId) return null;
  if (!getApps().length) {
    appSingleton = initializeApp(cfg);
  } else {
    appSingleton = getApps()[0];
  }
  return appSingleton;
}

export function getFirestoreDb() {
  if (dbSingleton) return dbSingleton;
  const app = getFirebaseApp();
  if (!app) return null;
  dbSingleton = getFirestore(app);
  return dbSingleton;
}

/** @returns {import('firebase/storage').FirebaseStorage | null} */
export function getFirebaseStorage() {
  if (storageSingleton) return storageSingleton;
  const app = getFirebaseApp();
  if (!app) return null;
  const bucketId = firebaseStorageBucketId();
  if (!bucketId) return null;
  // Bind bucket explicitly so Storage works even if the default app was created without storageBucket
  // (e.g. cached bundle) and to match console bucket names (*.firebasestorage.app vs *.appspot.com).
  storageSingleton = getStorage(app, `gs://${bucketId}`);
  return storageSingleton;
}

export function getFirebaseAuth() {
  if (authSingleton) return authSingleton;
  const app = getFirebaseApp();
  if (!app) return null;
  authSingleton = getAuth(app);
  return authSingleton;
}

export function isFirebaseSignedIn() {
  const auth = getFirebaseAuth();
  return Boolean(auth?.currentUser);
}

/**
 * True if the user may open /admin: Google account email is in `config/registryAdmins.allowedEmails`.
 * Matches guest-registry write access (see firestore.rules).
 */
export async function assertCanAccessGuestRegistryAdmin() {
  const auth = getFirebaseAuth();
  const db = getFirestoreDb();
  const user = auth?.currentUser;
  if (!db || !user?.email) {
    return false;
  }
  try {
    const snap = await getDoc(doc(db, 'config', 'registryAdmins'));
    if (!snap.exists()) {
      return false;
    }
    const data = snap.data();
    const allowed = data?.allowedEmails;
    if (!Array.isArray(allowed)) {
      return false;
    }
    const lower = user.email.toLowerCase();
    return allowed.some((e) => String(e || '').trim().toLowerCase() === lower);
  } catch {
    return false;
  }
}

/**
 * @returns {() => void} unsubscribe
 */
export function startRemoteGuestRegistryListener() {
  if (!isRemoteGuestRegistryConfigured()) {
    return () => {};
  }

  const db = getFirestoreDb();
  if (!db) {
    applyRemoteGuestRegistrySnapshot({ guests: null, docExists: false, hydrated: true });
    return () => {};
  }

  const { collectionId, docId } = parseRegistryPath();
  const docRef = doc(db, collectionId, docId);

  if (unsubSnapshot) {
    unsubSnapshot();
    unsubSnapshot = null;
  }

  unsubSnapshot = onSnapshot(
    docRef,
    (snap) => {
      if (!snap.exists()) {
        applyRemoteGuestRegistrySnapshot({ guests: null, docExists: false, hydrated: true });
        return;
      }
      const data = snap.data();
      const guests = coerceFirestoreGuestsToArray(data?.guests);
      applyRemoteGuestRegistrySnapshot({
        guests,
        docExists: true,
        hydrated: true,
        serverUpdatedAt: data?.updatedAt ?? null,
      });
    },
    (err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[wedding] Firestore guest registry listener error', err?.code || err?.message, err);
      }
      applyRemoteGuestRegistryListenError();
    },
  );

  return () => {
    if (unsubSnapshot) {
      unsubSnapshot();
      unsubSnapshot = null;
    }
  };
}

export async function persistRemoteGuests(guests) {
  const db = getFirestoreDb();
  const auth = getFirebaseAuth();
  if (!db || !auth?.currentUser) {
    throw new Error('Not signed in to Firebase');
  }
  const { collectionId, docId } = parseRegistryPath();
  const docRef = doc(db, collectionId, docId);
  const baseline = getRemoteRegistryWriteBaseline();
  const tabExpectsDoc = getRemoteRegistryDocExistsForWriteGuard();

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(docRef);
    const snapExists = snap.exists();

    if (tabExpectsDoc !== snapExists) {
      throw new Error('guest-registry-stale-baseline');
    }

    if (!snapExists) {
      transaction.set(docRef, { guests, updatedAt: serverTimestamp() });
      return;
    }

    const data = snap.data();
    const serverTs = data?.updatedAt ?? null;
    if (baseline != null) {
      if (serverTs == null || !firestoreTimestampsEqual(serverTs, baseline)) {
        throw new Error('guest-registry-stale-baseline');
      }
    }

    transaction.set(
      docRef,
      {
        guests,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  // Avoid stale baselines: plain getDoc can return a cached doc with an old updatedAt right after commit,
  // so the next save (e.g. bulk delete) falsely fails the optimistic lock. Always ack from the server.
  try {
    const after = await getDocFromServer(docRef);
    if (after.exists()) {
      const nextTs = after.data()?.updatedAt;
      if (nextTs) {
        ackRemoteRegistryDocUpdatedAt(nextTs);
      }
    }
  } catch {
    const after = await getDoc(docRef);
    if (after.exists()) {
      const nextTs = after.data()?.updatedAt;
      if (nextTs) {
        ackRemoteRegistryDocUpdatedAt(nextTs);
      }
    }
  }
}

export function subscribeFirebaseAuth(callback) {
  const auth = getFirebaseAuth();
  if (!auth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

/**
 * If signInWithRedirect was ever used, completes the OAuth round-trip. Safe on every load.
 * With a custom site domain and default `*.firebaseapp.com` authDomain, redirect sign-in is broken in
 * storage-partitioned browsers ("missing initial state"); Google sign-in uses popup only instead.
 */
export async function consumeFirebaseRedirectResult() {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  try {
    return await getRedirectResult(auth);
  } catch {
    return null;
  }
}

export async function firebaseSignInWithGoogle() {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('Firebase not configured');
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  await signInWithPopup(auth, provider);
}

export async function firebaseSignOut() {
  const auth = getFirebaseAuth();
  if (!auth) return;
  await signOut(auth);
}
